import { TagsMenu } from "@/components/ui/tags/tags-menu";
import { useProjectTagOptions } from "@/components/ui/tags/use-project-tag-options";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { Tag } from "@/store/tags/tags-slice";
import { selectShowSpinner } from "@/store/ui/ui-selectors";
import { setShowSpinner } from "@/store/ui/ui-slice";
import {
  FaroButton,
  FaroTooltip,
  Option,
  TagsAddIcon,
  ToolButton,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { useCallback, useMemo, useState, type JSX } from "react";
import { useAddTagsToIElements } from "../use-add-tags-to-ielements";

type AddTagsButtonProps = {
  /** The list of ids of the selected elements */
  selectedIds: GUID[];
};

/** @returns A toolbar button to assign the tags to the selected scans */
export function AddTagsButton({
  selectedIds,
}: AddTagsButtonProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [selectedTagOptions, setSelectedTagOptions] = useState<Option[]>([]);

  const dispatch = useAppDispatch();
  const showSpinner = useAppSelector(selectShowSpinner);
  const { handleErrorWithToast } = useErrorHandlers();

  const { tagOptions } = useProjectTagOptions({
    dark: true,
    disabled: showSpinner,
    onTagRemoved(tag) {
      // Remove the deleted tag from the selected tags
      setSelectedTagOptions((prev: Option[]) =>
        prev.filter((t) => t.key !== tag.id),
      );
    },
  });

  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);

  const selectedTags = useMemo<Tag[]>(
    () =>
      selectedTagOptions.map((option) => ({
        id: option.key,
        name: option.value,
      })),
    [selectedTagOptions],
  );
  const addTags = useAddTagsToIElements(selectedTags, selectedIds);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setSelectedTagOptions([]);
  }, []);

  const onAddTags = useCallback(async () => {
    dispatch(setShowSpinner(true));
    try {
      await addTags();
      closeMenu();
    } catch (error) {
      handleErrorWithToast({ title: "Could not add tags", error });
    } finally {
      dispatch(setShowSpinner(false));
    }
  }, [addTags, closeMenu, dispatch, handleErrorWithToast]);

  return (
    <>
      <FaroTooltip title="Add tags" placement="top">
        <ToolButton
          ref={setButtonRef}
          aria-label="add-tag"
          onClick={() => setIsMenuOpen(true)}
          selected={isMenuOpen}
        >
          <TagsAddIcon />
        </ToolButton>
      </FaroTooltip>
      <TagsMenu
        title="Add tags"
        anchorEl={buttonRef}
        isOpen={isMenuOpen}
        options={tagOptions}
        value={selectedTagOptions}
        onChange={setSelectedTagOptions}
        onClose={() => setIsMenuOpen(false)}
        disabled={showSpinner}
        dark
      >
        <FaroButton
          dark
          onClick={onAddTags}
          isLoading={showSpinner}
          size="m"
          disabled={selectedTagOptions.length === 0}
        >
          Add
        </FaroButton>
      </TagsMenu>
    </>
  );
}
